import { useState } from 'react';

import { Alert, ButtonMantine, Center, PasswordInput, Stack, TextInput } from 'ui';
import { isNotEmpty, useForm } from 'ui/Form';
import { IconGivebacks } from 'ui/Icons';

import { User, useLogInUser } from '../../api';
import { ResetPasswordModal } from '../Cards/Onboarding/ResetPasswordModal';
import { AuthFormProps } from '../Cards/Onboarding/userAuthCard.types';

type SignInProps = {
  initialEmail?: string;
  onSuccess?: (user: User) => void;
};

export const SignInForm = ({ initialEmail, onSuccess }: SignInProps) => {
  const { mutate: login, isPending: isLoading, isError } = useLogInUser();
  const [error, setError] = useState('');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const form = useForm<AuthFormProps>({
    initialValues: {
      email: initialEmail || '',
      password: '',
    },
    validate: {
      email: isNotEmpty('Invalid Email'),
      password: isNotEmpty('Invalid Password'),
    },
  });

  const handleLogIn = () => {
    if (form.validate().hasErrors) return;
    else {
      const { email, password } = form.values;
      const requestObject = {
        user: {
          email,
          password,
        },
      };
      login(requestObject, {
        onSuccess: (data) => {
          onSuccess && onSuccess(data);
        },
        onError: (e: Error) => {
          const error = e as { response?: { data?: { error?: string } } };
          setError(error?.response?.data?.error ?? 'Unknown error occurred, please try again.');
        },
      });
    }
  };

  return (
    <>
      <Stack gap="lg" py={'md'}>
        <Stack gap="lg">
          <TextInput
            placeholder="Email Address"
            leftSection={<IconGivebacks />}
            {...form.getInputProps('email')}
          />
          <PasswordInput placeholder="Password" {...form.getInputProps('password')} />
        </Stack>
        <div className="w-full">
          <Alert.Transition title={error} type="error" mounted={isError} />
        </div>
        <Stack gap={'xs'}>
          <ButtonMantine variant="gradient" onClick={handleLogIn} disabled={isLoading}>
            Sign In
          </ButtonMantine>
          <Center>
            <ButtonMantine
              variant="subtle"
              c={'gray.8'}
              onClick={() => setShowResetPasswordModal(true)}
            >
              I forgot my password
            </ButtonMantine>
          </Center>
        </Stack>
      </Stack>
      <ResetPasswordModal
        opened={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />
    </>
  );
};
